import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import type { GridPaginationModel } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import type { UseCentrosCustoResult } from "../hooks/useCentrosCusto";
import type { UseContasPagarResult } from "../hooks/useContasPagar";
import type { UseFornecedoresFinanceiroResult } from "../hooks/useFornecedoresFinanceiro";
import type { ContaPagarView } from "../types/contas-pagar.types";
import type { ContasPagarFiltros as CpFiltrosState } from "../types/contas-pagar.types";
import FinanceiroErrorState from "./FinanceiroErrorState";
import ContasPagarFiltros from "./ContasPagarFiltros";
import ContasPagarResumoCards from "./ContasPagarResumoCards";
import ContasPagarTabela from "./ContasPagarTabela";

function CardsSkeleton() {
  return (
    <Grid container spacing={2}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rounded" height={112} sx={{ borderRadius: 2 }} />
        </Grid>
      ))}
    </Grid>
  );
}

export type ContasPagarSectionProps = {
  contasPagar: UseContasPagarResult;
  centros: UseCentrosCustoResult;
  fornecedores: UseFornecedoresFinanceiroResult;
  onBaixarTitulo?: (titulo: ContaPagarView) => void;
  onCancelarTitulo?: (titulo: ContaPagarView) => void;
  onEditarTitulo?: (titulo: ContaPagarView) => void;
  /** Oculta o bloco de filtros operacionais (ex.: despesas do evento). */
  ocultarFiltros?: boolean;
  /** Oculta os cards de resumo (ex.: relatório por evento). */
  ocultarCards?: boolean;
  tabelaVariant?: "completa" | "evento" | "operacional";
  /** Tabela sem Card próprio (dentro de outro bloco). */
  tabelaEmbedded?: boolean;
};

export default function ContasPagarSection({
  contasPagar,
  centros,
  fornecedores,
  onBaixarTitulo,
  onCancelarTitulo,
  onEditarTitulo,
  ocultarFiltros = false,
  ocultarCards = false,
  tabelaVariant = "completa",
  tabelaEmbedded = false,
}: ContasPagarSectionProps) {
  const [periodoResetTick, setPeriodoResetTick] = useState(0);

  const handleLimpar = useCallback(() => {
    contasPagar.limparFiltros();
    setPeriodoResetTick((n) => n + 1);
  }, [contasPagar]);

  const handlePaginationModelChange = useCallback(
    (m: GridPaginationModel) => {
      contasPagar.setPagination(m.page + 1, m.pageSize);
    },
    [contasPagar]
  );

  const setFiltrosWrapper: Dispatch<SetStateAction<CpFiltrosState>> =
    useCallback(
      (action) => {
        contasPagar.setFiltros(action);
      },
      [contasPagar]
    );

  const { loading, error, isEmpty, isFiltered, data } = contasPagar;

  return (
    <Box component="section">
      <Stack spacing={2}>
        {ocultarFiltros ? null : centros.error ? (
          <Alert severity="warning">
            Não foi possível carregar centros de custo para o filtro:{" "}
            {centros.error}
          </Alert>
        ) : null}
        {ocultarFiltros ? null : fornecedores.error ? (
          <Alert severity="warning">
            Não foi possível carregar fornecedores para o filtro:{" "}
            {fornecedores.error}
          </Alert>
        ) : null}

        {error ? (
          <FinanceiroErrorState message={error} onRetry={contasPagar.refetch} />
        ) : null}

        {!error && loading && !data && !ocultarCards ? <CardsSkeleton /> : null}
        {!error && loading && !data && ocultarCards ? (
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
        ) : null}

        {!ocultarFiltros ? (
          <ContasPagarFiltros
            filtros={contasPagar.filtros}
            setFiltros={setFiltrosWrapper}
            onAplicar={contasPagar.aplicarFiltros}
            onLimpar={handleLimpar}
            loading={loading}
            centros={centros.data}
            centrosLoading={centros.loading}
            fornecedores={fornecedores.data}
            fornecedoresLoading={fornecedores.loading}
            resetPeriodoPresetTick={periodoResetTick}
          />
        ) : null}

        {!error && data ? (
          <>
            {!ocultarCards ? (
              <ContasPagarResumoCards resumo={data.resumo} loading={loading} />
            ) : null}
            {!loading && isEmpty ? (
              <MDBox
                py={6}
                textAlign="center"
                sx={{
                  border: "1px dashed",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <PaymentsOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  Nenhum título a pagar encontrado
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  {isFiltered
                    ? "Ajuste o período ou limpe os filtros para ver outros títulos."
                    : "Não há títulos a pagar registrados no período atual."}
                </MDTypography>
              </MDBox>
            ) : null}
            {!isEmpty ? (
              <Box>
                <ContasPagarTabela
                  linhas={data.itens}
                  total={data.total}
                  page={contasPagar.page}
                  limit={contasPagar.limit}
                  loading={loading}
                  variant={tabelaVariant}
                  embedded={tabelaEmbedded}
                  onPaginationModelChange={handlePaginationModelChange}
                  onBaixar={onBaixarTitulo}
                  onCancelar={onCancelarTitulo}
                  onEditar={onEditarTitulo}
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Box>
  );
}
